import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  Company,
  Location,
  OrderDirection,
} from "../../../../bookmydesk-api-sdk-typescript-axios";
import Button, {
  ButtonsWrapper,
  ButtonWrapper,
} from "../../../components/Button";
import Container from "../../../components/Container";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import LoadOverlay from "../../../components/LoadOverlay";
import {
  getOfficePages,
  getSaltoPage,
} from "../../../components/Navbar/AdminNavBarContent";
import Notification from "../../../components/Notification";
import PageSelector from "../../../components/PageSelector";
import Paginator, { takeSize } from "../../../components/Paginator";
import Table, { TableContent } from "../../../components/Table";
import {
  createHeaderItem,
  HeaderItem,
} from "../../../components/Table/TableHeaderItem";
import { useAuth } from "../../../context/authContext";
import { useApi } from "../../../hooks/useApi";
import { useClient } from "../../../hooks/useClient";
import { t } from "../../../i18n";
import { RouterProps, useHistory } from "../../../routing";
import { OverviewHeader } from "../../../styles/Overview";
import { ExtraSmallText } from "../../../styles/Text";
import { color, wpdColor } from "../../../styles/theme";
import Title from "../../../styles/Title";
import { ArrayElement, ThenArg } from "../../../types";

const columnSizes = [2, 3, 2, 3, 1, 1, 1];

export const ResourceOverview: FC<RouterProps> = () => {
  const tableHeaders: HeaderItem[] = [
    createHeaderItem(t("admin.parking.overview.name"), false),
    createHeaderItem(t("admin.parking.overview.description"), false),
    createHeaderItem(t("admin.parking.overview.location"), false),
    createHeaderItem(t("admin.parking.overview.resourceType"), false),
    createHeaderItem(t("admin.parking.overview.active"), false),
    createHeaderItem(t("admin.parking.overview.amountAvailable"), false),
    createHeaderItem(
      t("admin.parking.overview.reservationConnectionEnabled"),
      false
    ),
    createHeaderItem(t("admin.parking.overview.customFields"), false),
  ];
  const { userState } = useAuth();
  const client = useClient();

  type ResourceItem = ArrayElement<
    ThenArg<ReturnType<typeof client.listResources>>["data"]["result"]["items"]
  >;

  const { handleRequest, isLoading, error, dismissError } = useApi();
  const userCompany = userState?.companyIds[0];
  const [data, setData] = useState<ResourceItem[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [tableData, setTableData] = useState<Array<TableContent>>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [skipParam, setSkipParam] = useState<number>(0);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemSelected, setItemSelected] = useState<ResourceItem>();
  const history = useHistory();

  const fetchResources = useCallback(async () => {
    if (userCompany) {
      return await handleRequest(
        client.listResources(userCompany, takeSize, skipParam)
      );
    }
  }, [userCompany, handleRequest, client, skipParam]);

  const fetchCompany = useCallback(async () => {
    if (userCompany) {
      return await handleRequest(client.getCompany(userCompany));
    }
  }, [userCompany, handleRequest, client]);

  const fetchLocations = useCallback(async () => {
    if (userCompany) {
      return await handleRequest(client.listLocations(userCompany));
    }
  }, [userCompany, handleRequest, client]);

  const initialize = useCallback(() => {
    fetchResources().then((result) => {
      if (!result || !result.data.result) {
        return;
      }

      const { items, total } = result.data.result;
      if (items) {
        setData(items || []);
        setTotalItems(total);
      }
    });

    fetchLocations().then((result) => {
      if (!result || !result.data.result) {
        return;
      }

      const { items } = result.data.result;
      if (items) {
        setLocations(items || []);
      }
    });

    fetchCompany().then((result) => {
      if (!result || !result.data.result) {
        return;
      }

      const { company } = result.data.result;
      if (company) {
        setCompany(company as Company);
      }
    });
  }, [fetchCompany, fetchResources, fetchLocations]);

  const move = useCallback(
    async (location: ResourceItem, direction: OrderDirection) => {
      await handleRequest(
        client.changeLocationOrder(location.id, {
          direction,
        }),
        false
      );
      initialize();
    },
    [client, handleRequest, initialize]
  );

  const renderTablePages = useCallback(() => {
    if (company?.saltoUsername && company?.saltoPassword) {
      return [...getOfficePages(), ...getSaltoPage()];
    }
    return getOfficePages();
  }, [company]);

  const createTableData = useCallback(
    (items: ResourceItem[]) => {
      const isLastPage = takeSize + skipParam + 1 > totalItems;
      const tableData: TableContent[] = items.map(
        (
          {
            name,
            locationId,
            id,
            description,
            resourceType,
            active,
            amountAvailable,
            reservationConnectionEnabled,
            customFields,
            _operations,
          },
          index,
          currentArray
        ) => {
          const data: ReactElement[] = [
            <ExtraSmallText key="name" mediumWeight>
              {name}
            </ExtraSmallText>,
            <ExtraSmallText key="description" mediumWeight>
              {description}
            </ExtraSmallText>,
            <ExtraSmallText key="location" mediumWeight>
              {locations.find((location) => location.id === locationId)?.name}
            </ExtraSmallText>,
            <ExtraSmallText key="resourceType" mediumWeight>
              {resourceType}
            </ExtraSmallText>,
            <ExtraSmallText key="active" mediumWeight>
              {active ? "Yes" : "No"}
            </ExtraSmallText>,
            <ExtraSmallText key="amountAvailable" mediumWeight>
              {amountAvailable}
            </ExtraSmallText>,
            <ExtraSmallText key="reservationConnectionEnabled" mediumWeight>
              {reservationConnectionEnabled ? "Yes" : "No"}
            </ExtraSmallText>,
            <ExtraSmallText key="customFields" mediumWeight>
              {customFields.map((field) => field.name).join(", ")}
            </ExtraSmallText>,
            <ButtonsWrapper key="buttons">
              {totalItems !== 1 &&
                ((skipParam === 0 && index !== 0) || skipParam !== 0) && (
                  <ButtonWrapper>
                    <Button
                      backgroundColor={wpdColor.backgroundLighter}
                      backgroundHoverColor={color.secondary}
                      borderColor={color.secondary}
                      textHoverColor={wpdColor.backgroundLighter}
                      onPress={() => move(items[index], OrderDirection.Up)}
                    >
                      {t("general.up")}
                    </Button>
                  </ButtonWrapper>
                )}
              {totalItems !== 1 &&
                (!isLastPage ||
                  (isLastPage && index !== currentArray.length - 1)) && (
                  <ButtonWrapper>
                    <Button
                      backgroundColor={wpdColor.backgroundLighter}
                      backgroundHoverColor={color.secondary}
                      borderColor={color.secondary}
                      textHoverColor={wpdColor.backgroundLighter}
                      onPress={() => move(items[index], OrderDirection.Down)}
                    >
                      {t("general.down")}
                    </Button>
                  </ButtonWrapper>
                )}
            </ButtonsWrapper>,
          ];
          return {
            data,
            id,
            canDelete: true,
            cannotDeleteDescription: _operations?.cannotDeleteDescription,
            canEdit: true,
          };
        }
      );
      return tableData;
    },
    [skipParam, totalItems, move, locations]
  );

  useEffect(() => {
    setTableData(createTableData(data));
  }, [createTableData, data]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const getItemById = useCallback(
    (itemId: string) => data.find(({ id }) => id === itemId),
    [data]
  );

  const openDeleteModal = useCallback(
    (id: string) => {
      const item = getItemById(id);
      setItemSelected(item);
      setShowDeleteModal(true);
    },
    [setItemSelected, getItemById, setShowDeleteModal]
  );

  const deleteFromList = useCallback(async () => {
    setShowDeleteModal(false);

    if (!itemSelected) {
      return;
    }

    const response = await handleRequest(
      client.deleteResource(itemSelected.id)
    );

    if (response && response.status === 204) {
      await fetchResources().then((result) => {
        if (!result || !result.data.result) {
          return;
        }

        const { items, total } = result.data.result;
        if (items) {
          setTableData(createTableData(items));
          setData(items);
          setTotalItems(total);
        }
      });

      setShowDeleteMessage(true);
    }
  }, [handleRequest, client, itemSelected, fetchResources, createTableData]);

  return (
    <>
      {isLoading ? <LoadOverlay /> : null}
      <Container>
        <OverviewHeader>
          <Title>{t("admin.parking.overview.title")}</Title>
          <Button
            onPress={() => history.push("/admin/resource-overview/create")}
          >
            {t("admin.parking.overview.addButton")}
          </Button>
        </OverviewHeader>
        <PageSelector pages={renderTablePages()} />
        {Boolean(error) && (
          <Notification closeNotification={dismissError}>
            {t("general.error")}
          </Notification>
        )}
        {showDeleteMessage ? (
          <Notification
            type="success"
            closeNotification={() => setShowDeleteMessage(false)}
          >
            {t("general.deleteSuccess")}
          </Notification>
        ) : null}
        {isLoading ? null : (
          <Table
            tableId="resource_overview"
            headerItems={tableHeaders}
            tableContent={tableData}
            columnSizes={columnSizes}
            skipParam={skipParam}
            setSkipParam={setSkipParam}
            onEditItem={(id) =>
              history.push(`/admin/resource-overview/update/${id}`)
            }
            onDeleteItem={openDeleteModal}
          />
        )}
        {totalItems && !isLoading ? (
          <Paginator
            totalItems={totalItems}
            skipParam={skipParam}
            setSkipParam={setSkipParam}
          />
        ) : undefined}
      </Container>

      <ConfirmDialog
        confirm={deleteFromList}
        showDialog={showDeleteModal}
        setShowDialog={setShowDeleteModal}
        text={t("admin.parking.delete.warning")}
      />
    </>
  );
};
